<script lang="ts" setup>
import { Head, Link, useForm } from '@inertiajs/vue3'
import { FormKit } from '@formkit/vue'
// import ApplicationLogo from '@/components/ApplicationLogo.vue'
import Checkbox from '@/components/Checkbox.vue'
import InputError from '@/components/InputError.vue'
import InputLabel from '@/components/InputLabel.vue'
import LocationAlert from '@/components/LocationAlert.vue'
import PrimaryButton from '@/components/PrimaryButton.vue'
import TextInput from '@/components/TextInput.vue'
import route from 'ziggy-js'

const props = defineProps<{
  email?: string
}>()

const form = useForm({
  first_name: '',
  last_name: '',
  email: props.email,
  country: 'ZA',
  mobile_number: '',
  company: '',
  password: '',
  password_confirmation: '',
  terms: false,
})

const countryOptions = [
  { value: 'ZA', label: 'South Africa' },
  { value: 'US', label: 'United States' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'AU', label: 'Australia' },
  { value: 'CA', label: 'Canada' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'IT', label: 'Italy' },
  { value: 'ES', label: 'Spain' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'BE', label: 'Belgium' },
  { value: 'PT', label: 'Portugal' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'AT', label: 'Austria' },
  { value: 'IE', label: 'Ireland' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'SG', label: 'Singapore' },
  { value: 'MY', label: 'Malaysia' },
  { value: 'TH', label: 'Thailand' },
  { value: 'PH', label: 'Philippines' },
  { value: 'ID', label: 'Indonesia' },
  { value: 'IN', label: 'India' },
  { value: 'CN', label: 'China' },
  { value: 'JP', label: 'Japan' },
  { value: 'KR', label: 'South Korea' },
  { value: 'BR', label: 'Brazil' },
  { value: 'MX', label: 'Mexico' },
  { value: 'AR', label: 'Argentina' },
  { value: 'CL', label: 'Chile' },
  { value: 'CO', label: 'Colombia' },
]

const submit = () => {
  form.post(route('register'), {
    onFinish: () => form.reset('password', 'password_confirmation'),
  })
}
</script>

<template>
  <Head title="Register" />

  <div class="flex flex-col justify-center items-center sm:pt-0 bg-white-100 ">
    <div class="w-full sm:max-w-md  px-6 py-4 bg-white overflow-hidden sm:rounded-lg">
      <div class="divide-y">
        <div>
          <h5 class="text-2xl text-sky-500">Complete registration...</h5>
          <p class="text-sm text-sky-400 mt-1">It only takes 3 min </p>
        </div>
        <hr class="h-px my-4 bg-gray-200 border-0 dark:bg-gray-700" />
      </div>

      <LocationAlert class="mt-4" />

      <form
        @submit.prevent="submit"
        class="pt-4">
        <div>
          <InputLabel
            for="first_name"
            value="First name" />
          <TextInput
            id="first_name"
            v-model="form.first_name"
            autocomplete="given-name"
            autofocus
            class="mt-1 block w-full border-0 border-b-2 border-gray-300 focus:border-blue-500 focus:ring-0 rounded-none px-0"
            required
            type="text" />
          <InputError
            :message="form.errors.first_name"
            class="mt-2" />
        </div>

        <div class="mt-4">
          <InputLabel
            for="last_name"
            value="Last name" />
          <TextInput
            id="last_name"
            v-model="form.last_name"
            autocomplete="family-name"
            class="mt-1 block w-full border-0 border-b-2 border-gray-300 focus:border-blue-500 focus:ring-0 rounded-none px-0"
            required
            type="text" />
          <InputError
            :message="form.errors.last_name"
            class="mt-2" />
        </div>

        <div class="mt-4">
          <InputLabel
            for="email"
            value="Email address" />
          <TextInput
            id="email"
            v-model="form.email"
            autocomplete="username"
            class="mt-1 block w-full border-0 border-b-2 border-gray-300 focus:border-blue-500 focus:ring-0 rounded-none px-0"
            required
            type="email" />
          <InputError
            :message="form.errors.email"
            class="mt-2" />
        </div>

        <div class="mt-4">
          <InputLabel
            for="country"
            value="Country" />
          <FormKit
            v-model="form.country"
            :options="countryOptions"
            name="country"
            type="dropdown"
            outer-class="!mb-0"
            inner-class="!max-w-full !border-0 !border-b-2 !border-gray-300 focus-within:!border-blue-500 focus-within:!ring-0 !rounded-none !px-0 !shadow-none !mb-0"
            input-class="!w-full !px-0 !py-2 !text-gray-700 !text-sm !font-medium"
            label-class="hidden"
            wrapper-class="!mt-1" />
          <InputError
            :message="form.errors.country"
            class="mt-2" />
        </div>

        <div class="mt-4">
          <InputLabel
            for="mobile_number"
            value="Mobile number" />
          <TextInput
            id="mobile_number"
            v-model="form.mobile_number"
            autocomplete="tel"
            placeholder="e.g. +27831231234"
            class="mt-1 block w-full border-0 border-b-2 border-gray-300 focus:border-blue-500 focus:ring-0 rounded-none px-0"
            type="tel" />
          <InputError
            :message="form.errors.mobile_number"
            class="mt-2" />
        </div>

        <div class="mt-4">
          <InputLabel
            for="company"
            value="Company" />
          <TextInput
            id="company"
            v-model="form.company"
            autocomplete="organization"
            class="mt-1 block w-full border-0 border-b-2 border-gray-300 focus:border-blue-500 focus:ring-0 rounded-none px-0"
            type="text" />
          <InputError
            :message="form.errors.company"
            class="mt-2" />
        </div>

        <div class="mt-4">
          <InputLabel
            for="password"
            value="Password" />
          <TextInput
            id="password"
            v-model="form.password"
            autocomplete="new-password"
            class="mt-1 block w-full border-0 border-b-2 border-gray-300 focus:border-blue-500 focus:ring-0 rounded-none px-0"
            required
            type="password" />
          <InputError
            :message="form.errors.password"
            class="mt-2" />
        </div>

        <div class="mt-4">
          <InputLabel
            for="password_confirmation"
            value="Confirm password" />
          <TextInput
            id="password_confirmation"
            v-model="form.password_confirmation"
            autocomplete="new-password"
            class="mt-1 block w-full border-0 border-b-2 border-gray-300 focus:border-blue-500 focus:ring-0 rounded-none px-0"
            required
            type="password" />
          <InputError
            :message="form.errors.password_confirmation"
            class="mt-2" />
        </div>

        <div
          v-if="$page.props.jetstream.hasTermsAndPrivacyPolicyFeature"
          class="mt-4">
          <div class="flex items-center">
            <Checkbox
              id="terms"
              v-model:checked="form.terms"
              name="terms"
              required />

            <InputLabel
              for="terms"
              class="ms-2 mb-0">
              I agree to the
              <a
                :href="route('terms.show')"
                class="underline text-sm text-blue-500 hover:text-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                target="_blank"
                >terms and conditions</a
              >
            </InputLabel>
          </div>
          <InputError
            :message="form.errors.terms"
            class="mt-2" />
        </div>

        <div class="flex items-center justify-center mt-6">
          <PrimaryButton
            :class="{ 'opacity-25': form.processing }"
            :disabled="form.processing"
            class="w-full bg-gray-500 hover:bg-gray-600 text-white">
            START EARNING TODAY
          </PrimaryButton>
        </div>
      </form>

      <div class="mt-4 text-center">
        <span class="text-sm text-gray-600">Already a member? </span>
        <Link
          :href="route('login')"
          class="text-sm text-blue-500 hover:text-blue-700 underline">
          Login
        </Link>
      </div>
    </div>
  </div>
</template>
